import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  LayoutDashboard, School, Building2, BookOpen, UserCog, Briefcase,
  GraduationCap, Package, CalendarDays, Receipt, KeyRound, Settings,
  ScrollText, LifeBuoy, ClipboardCheck, Calculator, Sigma, MinusCircle,
  ReceiptText, Eye, History, Users,
} from "lucide-react";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { BranchProvider } from "@/contexts/BranchContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FontApplier from "@/components/FontApplier";
import DashboardLayout from "@/components/DashboardLayout";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import BranchDetailPage from "@/pages/BranchDetailPage";

import OwnerOverviewPage from "@/pages/owner/OwnerOverviewPage";
import SchoolProfilePage from "@/pages/owner/SchoolProfilePage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import BranchesPage from "@/pages/owner/BranchesPage";
import SettingsPage from "@/pages/owner/SettingsPage";
import SettingsTelegramPage from "@/pages/SettingsTelegramPage";
import EmployeeListPage from "@/pages/owner/employees/EmployeeListPage";
import EmployeesPlaceholderPage from "@/pages/owner/employees/EmployeesPlaceholderPage";
import SalaryModulePage from "@/pages/owner/employees/SalaryModulePage";
import SalaryFormulaPage from "@/pages/owner/employees/SalaryFormulaPage";
import EmployeeAttendancePage from "@/pages/owner/employees/EmployeeAttendancePage";
import EmployeeAttendanceDayPage from "@/pages/owner/employees/EmployeeAttendanceDayPage";
import PaySlipPage from "@/pages/owner/employees/PaySlipPage";
import PaySlipOverviewPage from "@/pages/owner/employees/PaySlipOverviewPage";
import PaySlipOverviewHistoryPage from "@/pages/owner/employees/PaySlipOverviewHistoryPage";
import EmployeeOtherDeductionsPage from "@/pages/owner/employees/EmployeeOtherDeductionsPage";
import ClassesSectionsPage from "@/pages/owner/ClassesSectionsPage";
import StudentsPage from "@/pages/owner/students/StudentsPage";
import PendingEnrollmentsPage from "@/pages/owner/students/PendingEnrollmentsPage";
import ProfileUpdatesPage from "@/pages/owner/students/ProfileUpdatesPage";
import StudentAccountsPage from "@/pages/owner/students/StudentAccountsPage";
import StatementOfAccountPage from "@/pages/owner/students/StatementOfAccountPage";
import StudentAccountsSummaryPage from "@/pages/owner/students/StudentAccountsSummaryPage";
import ClassAttendancePage from "@/pages/owner/students/ClassAttendancePage";
import IndividualAttendancePage from "@/pages/owner/students/IndividualAttendancePage";
import GradingSetupPage from "@/pages/owner/grading/GradingSetupPage";
import GradesPage from "@/pages/owner/grading/GradesPage";
import ReportCardPage from "@/pages/owner/grading/ReportCardPage";
import RankingsPage from "@/pages/owner/grading/RankingsPage";
import ProgressFeedbackPage from "@/pages/owner/progress-feedback/ProgressFeedbackPage";
import TuitionPlansPage from "@/pages/owner/tuition/TuitionPlansPage";
import AccountingIncomePage from "@/pages/owner/accounting/AccountingIncomePage";
import AccountingExpensesPage from "@/pages/owner/accounting/AccountingExpensesPage";
import CashAnalysisPage from "@/pages/owner/accounting/CashAnalysisPage";
import AccountingReportsPage from "@/pages/owner/accounting/AccountingReportsPage";
import DailyCashReportPage from "@/pages/owner/accounting/DailyCashReportPage";
import BranchAdminsPage from "@/pages/owner/BranchAdminsPage";
import SpecialAccessPage from "@/pages/owner/SpecialAccessPage";
import ActivityLogsPage from "@/pages/owner/ActivityLogsPage";
import PublicSchoolLandingPage from "@/pages/public/PublicSchoolLandingPage";
import PublicEnrollPage from "@/pages/public/PublicEnrollPage";
import LandingSettingsPage from "@/pages/owner/LandingSettingsPage";
import ParentPortalSettingsPage from "@/pages/owner/ParentPortalSettingsPage";
import SuppliesPage from "@/pages/owner/SuppliesPage";
import EventsPage from "@/pages/owner/EventsPage";
import BranchDocumentsPage from "@/pages/owner/BranchDocumentsPage";
import InquiriesPage from "@/pages/owner/InquiriesPage";
import StudentIdCardPage from "@/pages/owner/StudentIdCardPage";
import AttendanceScannerPage from "@/pages/owner/AttendanceScannerPage";
import PaymentLedgerPage from "@/pages/owner/students/PaymentLedgerPage";

import ParentDashboardPage from "@/pages/parent/ParentDashboardPage";
import ParentChangePasswordPage from "@/pages/parent/ParentChangePasswordPage";

import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfUsePage from "@/pages/legal/TermsOfUsePage";

import ConsentBanner from "@/components/ConsentBanner";

import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminApprovalsPage from "@/pages/admin/AdminApprovalsPage";
import AdminSchoolsPage from "@/pages/admin/AdminSchoolsPage";
import SiteContentPage from "@/pages/admin/SiteContentPage";
import SubAdminsManagementPage from "@/pages/admin/SubAdminsManagementPage";
import SendNotificationPage from "@/pages/admin/SendNotificationPage";
import NotificationsPage from "@/pages/NotificationsPage";
import HelpSupportPage from "@/pages/HelpSupportPage";
import IncidentsPage from "@/pages/IncidentsPage";
import MemosPage from "@/pages/MemosPage";
import KPIsPage from "@/pages/KPIsPage";
import AdminSubscriptionsPage from "@/pages/admin/AdminSubscriptionsPage";
import AdminSubscriptionPaymentsPage from "@/pages/admin/AdminSubscriptionPaymentsPage";
import BillingPage from "@/pages/owner/BillingPage";
import InsightsPage from "@/pages/InsightsPage";
import ModuleGate from "@/components/ModuleGate";

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <LandingPage />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  if (user.role === "parent") {
    return <Navigate to={user.must_change_password ? "/parent/change-password" : "/parent"} replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// Roles allowed on shared owner pages a branch_admin / special_access may also see
const OWNER_OR_STAFF = ["school_owner", "branch_admin", "special_access"];

const ownerPlaceholders = [];

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
          <BranchProvider>
            <FontApplier />
            <Toaster
              position="bottom-right"
              richColors
              toastOptions={{ className: "font-body rounded-2xl" }}
            />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Public school landing page */}
            <Route path="/school/:slug" element={<PublicSchoolLandingPage />} />
            <Route path="/enroll/:branchId" element={<PublicEnrollPage />} />
            <Route path="/enroll/:schoolSlug/:branchSlug" element={<PublicEnrollPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfUsePage />} />

            {/* School Owner routes */}
            <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<ProtectedRoute role={OWNER_OR_STAFF}><OwnerOverviewPage /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="branch_documents_view"><BranchDocumentsPage /></ProtectedRoute>} />
            <Route path="/inquiries" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="inquiries_view"><InquiriesPage /></ProtectedRoute>} />
            <Route path="/students/:studentId/id-card" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="students_view"><StudentIdCardPage /></ProtectedRoute>} />
            <Route path="/id-cards" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="students_view"><StudentIdCardPage /></ProtectedRoute>} />
            <Route path="/attendance/scan" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="attendance_mark"><AttendanceScannerPage /></ProtectedRoute>} />
            <Route path="/students/program-assessment" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="program_assessment_view"><PaymentLedgerPage mode="program_assessment" /></ProtectedRoute>} />
            <Route path="/students/retention-fee" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="retention_fee_view"><PaymentLedgerPage mode="retention_fee" /></ProtectedRoute>} />
            <Route path="/change-password" element={<ProtectedRoute role={["super_admin", "sub_super_admin", "school_owner", "sub_owner", "branch_admin", "special_access"]}><ChangePasswordPage /></ProtectedRoute>} />
            <Route path="/help-center" element={<ProtectedRoute role={["super_admin", "sub_super_admin", "school_owner", "sub_owner", "branch_admin", "special_access"]}><HelpCenterPage /></ProtectedRoute>} />
            <Route path="/school-profile" element={<ProtectedRoute role="school_owner"><SchoolProfilePage /></ProtectedRoute>} />
            <Route path="/branches" element={<ProtectedRoute role="school_owner"><BranchesPage /></ProtectedRoute>} />
            <Route path="/branches/:branchId" element={<ProtectedRoute role="school_owner"><BranchDetailPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute role="school_owner"><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/telegram" element={<ProtectedRoute><SettingsTelegramPage /></ProtectedRoute>} />

            {/* Classes & Sections */}
            <Route path="/classes" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="classes_view"><ClassesSectionsPage /></ProtectedRoute>} />

            {/* Students */}
            <Route path="/students" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="students_view"><StudentsPage /></ProtectedRoute>} />
            <Route path="/students/pending" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="students_view"><PendingEnrollmentsPage /></ProtectedRoute>} />
            <Route path="/students/profile-updates" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="students_view"><ProfileUpdatesPage /></ProtectedRoute>} />
            <Route path="/students/accounts" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="student_accounts_view"><StudentAccountsPage /></ProtectedRoute>} />
            <Route path="/students/accounts/statement-of-account" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="statement_of_account_view"><StatementOfAccountPage /></ProtectedRoute>} />
            <Route path="/students/accounts/summary" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="student_accounts_view"><StudentAccountsSummaryPage /></ProtectedRoute>} />
            <Route path="/students/attendance" element={<ProtectedRoute role={OWNER_OR_STAFF} permission={["attendance_view", "attendance_mark"]}><ClassAttendancePage /></ProtectedRoute>} />
            <Route path="/students/individual-attendance" element={<ProtectedRoute role={OWNER_OR_STAFF} permission={["attendance_view", "attendance_mark"]}><IndividualAttendancePage /></ProtectedRoute>} />
            <Route path="/students/grading-setup" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="grading_setup_view"><GradingSetupPage /></ProtectedRoute>} />
            <Route path="/students/grades" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="grades_view"><GradesPage /></ProtectedRoute>} />
            <Route path="/students/report-cards" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="report_cards_view"><ReportCardPage /></ProtectedRoute>} />
            <Route path="/students/rankings" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="report_cards_view"><RankingsPage /></ProtectedRoute>} />
            <Route path="/students/progress-feedback" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="progress_feedback_view"><ModuleGate module="students.progress_feedback" title="Progress Feedback"><ProgressFeedbackPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/students/tuition-plans" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="tuition_view"><TuitionPlansPage /></ProtectedRoute>} />

            {/* Supplies & Inventories */}
            <Route path="/supplies" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="inventory_view"><SuppliesPage /></ProtectedRoute>} />

            {/* Events */}
            <Route path="/events" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="events_view"><EventsPage /></ProtectedRoute>} />

            {/* Accounting */}
            <Route path="/accounting" element={<Navigate to="/accounting/income" replace />} />
            <Route path="/accounting/income" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="accounting_income_view"><AccountingIncomePage /></ProtectedRoute>} />
            <Route path="/accounting/expenses" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="accounting_expenses_view"><AccountingExpensesPage /></ProtectedRoute>} />
            <Route path="/accounting/cash-analysis" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="cash_analysis_view"><ModuleGate module="accounting.cash_analysis" title="Cash Analysis"><CashAnalysisPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/accounting/daily-cash-report" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="accounting_income_view"><DailyCashReportPage /></ProtectedRoute>} />
            <Route path="/accounting/reports" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="reports_view"><ModuleGate module="accounting.reports" title="Accounting Reports"><AccountingReportsPage /></ModuleGate></ProtectedRoute>} />

            {/* Branch Admins management */}
            <Route path="/branch-admins" element={<ProtectedRoute role="school_owner"><BranchAdminsPage /></ProtectedRoute>} />
            <Route path="/special-access" element={<ProtectedRoute role="school_owner"><SpecialAccessPage /></ProtectedRoute>} />
            <Route path="/activity-logs" element={<ProtectedRoute role="school_owner"><ActivityLogsPage /></ProtectedRoute>} />
            <Route path="/landing-settings" element={<ProtectedRoute role="school_owner"><LandingSettingsPage /></ProtectedRoute>} />
            <Route path="/parent-portal-settings" element={<ProtectedRoute role={OWNER_OR_STAFF} permission={["parent_portal_view", "parent_portal_manage"]}><ParentPortalSettingsPage /></ProtectedRoute>} />

            {/* Parent portal */}
            </Route>
            <Route path="/parent" element={<ProtectedRoute role="parent"><ParentDashboardPage /></ProtectedRoute>} />
            <Route path="/parent/change-password" element={<ProtectedRoute role="parent"><ParentChangePasswordPage /></ProtectedRoute>} />
            <Route element={<DashboardLayout />}>

            {/* Employees module */}
            <Route path="/employees" element={<Navigate to="/employees/list" replace />} />
            <Route path="/employees/list" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="employees_view"><EmployeeListPage /></ProtectedRoute>} />
            <Route path="/employees/attendance" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="employee_attendance_view"><EmployeeAttendancePage /></ProtectedRoute>} />
            <Route path="/employees/attendance/:date" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="employee_attendance_view"><EmployeeAttendanceDayPage /></ProtectedRoute>} />
            <Route path="/employees/salary-module" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="salary_view"><SalaryModulePage /></ProtectedRoute>} />
            <Route path="/employees/salary-formula" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="salary_formula_view"><SalaryFormulaPage /></ProtectedRoute>} />
            <Route path="/employees/other-deductions" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="employee_deductions_view"><EmployeeOtherDeductionsPage /></ProtectedRoute>} />
            <Route path="/employees/payslip" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="payslip_view"><ModuleGate module="employees.payslip" title="Pay Slip"><PaySlipPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/employees/payslip-overview" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="payslip_overview_view"><ModuleGate module="employees.payslip_overview" title="Pay Slip Overview"><PaySlipOverviewPage /></ModuleGate></ProtectedRoute>} />
            <Route path="/employees/payslip-history" element={<ProtectedRoute role={OWNER_OR_STAFF} permission="payslip_history_view"><ModuleGate module="employees.payslip_history" title="Pay Slip History"><PaySlipOverviewHistoryPage /></ModuleGate></ProtectedRoute>} />
            {ownerPlaceholders.map((p) => (
              <Route
                key={p.path}
                path={p.path}
                element={
                  <ProtectedRoute role="school_owner">
                    <PlaceholderPage title={p.title} description={p.desc} icon={p.icon} />
                  </ProtectedRoute>
                }
              />
            ))}

            {/* Super Admin routes */}
            <Route path="/admin" element={<ProtectedRoute role="super_admin"><AdminOverviewPage /></ProtectedRoute>} />
            <Route path="/admin/schools-branches" element={<ProtectedRoute role="super_admin"><AdminSchoolsPage /></ProtectedRoute>} />
            <Route path="/admin/approvals" element={<ProtectedRoute role="super_admin"><AdminApprovalsPage /></ProtectedRoute>} />
            <Route path="/admin/site-content" element={<Navigate to="/admin/settings" replace />} />
            <Route path="/admin/sub-admins" element={<ProtectedRoute role={["super_admin", "sub_super_admin"]}><SubAdminsManagementPage scope="super" backLink="/admin" pageTitle="Sub-Super Admins" /></ProtectedRoute>} />
            <Route path="/owner/sub-owners" element={<ProtectedRoute role={["school_owner", "sub_owner"]}><SubAdminsManagementPage scope="owner" backLink="/dashboard" pageTitle="Sub-Owners" /></ProtectedRoute>} />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute role={["super_admin", "sub_super_admin"]}>
                  <SiteContentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/send-notification"
              element={
                <ProtectedRoute role={["super_admin", "sub_super_admin"]}>
                  <SendNotificationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute role={["super_admin", "sub_super_admin", "school_owner", "sub_owner", "branch_admin", "special_access"]}>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute role={["super_admin", "sub_super_admin", "school_owner", "sub_owner", "branch_admin", "special_access"]}>
                  <HelpSupportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/incidents"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <IncidentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/incidents/:id"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <IncidentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memos"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <MemosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memos/:id"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <MemosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kpis"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <ModuleGate module="kpis" title="KPI Targets"><KPIsPage /></ModuleGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/kpis/:id"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin"]}>
                  <ModuleGate module="kpis" title="KPI Targets"><KPIsPage /></ModuleGate>
                </ProtectedRoute>
              }
            />

            {/* Subscription / Billing */}
            <Route
              path="/admin/subscriptions"
              element={<ProtectedRoute role={["super_admin", "sub_super_admin"]}><AdminSubscriptionsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/subscription-payments"
              element={<ProtectedRoute role={["super_admin", "sub_super_admin"]}><AdminSubscriptionPaymentsPage /></ProtectedRoute>}
            />
            <Route
              path="/billing"
              element={<ProtectedRoute role={["school_owner", "sub_owner"]}><BillingPage /></ProtectedRoute>}
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute role={["school_owner", "sub_owner", "branch_admin", "super_admin", "sub_super_admin"]}>
                  <ModuleGate module="ai_insights" title="AI School Insights"><InsightsPage /></ModuleGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/insights"
              element={
                <ProtectedRoute role={["super_admin", "sub_super_admin"]}>
                  <InsightsPage />
                </ProtectedRoute>
              }
            />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ConsentBanner />
          </BranchProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
